import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MessageCircle, X, CalendarCheck2 } from "lucide-react";
import { business } from "@/data/business";
import { trackEvent } from "@/lib/analytics";

const FloatingContact = () => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const telHref = `tel:${business.phone.replace(/\s+/g, "")}`;
  const mailHref = `mailto:${business.email}`;

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      className="fixed bottom-5 right-5 md:bottom-8 md:right-8 z-[60] flex flex-col items-end gap-3"
      data-testid="floating-contact"
    >
      {/* Options — mounted only when open, animated in */}
      {open && (
        <div className="flex flex-col items-end gap-3" role="menu" aria-label="Contact options">
          <Link
            to="/for-schools#enquiry"
            onClick={() => {
              trackEvent("floating_contact_book_click", { location: "floating-menu", cta: "book-visit" });
              setOpen(false);
            }}
            className="fc-action flex items-center gap-3 pl-4 pr-5 py-3 rounded-full bg-[#FFD45C] shadow-[0_12px_30px_rgba(255,212,92,0.35)] text-[#14324A] font-semibold hover:bg-[#FFC02A] transition-colors"
            data-testid="floating-contact-book"
            data-analytics="floating_contact_book"
            style={{ animationDelay: "0ms" }}
          >
            <span className="w-9 h-9 rounded-full bg-[#14324A] text-white inline-flex items-center justify-center">
              <CalendarCheck2 size={18} strokeWidth={2.5} />
            </span>
            <span className="flex flex-col leading-tight text-left">
              <span className="text-xs uppercase tracking-widest text-[#14324A]/70">Book</span>
              <span className="text-sm">School visit</span>
            </span>
          </Link>
          <a
            href={telHref}
            onClick={() => {
              trackEvent("floating_contact_call_click", { location: "floating-menu", cta: "call", phone: business.phone });
              setOpen(false);
            }}
            className="fc-action group flex items-center gap-3 pl-4 pr-5 py-3 rounded-full bg-white shadow-[0_12px_30px_rgba(20,50,74,0.18)] border border-[#E5F0FA] text-[#14324A] font-semibold hover:bg-[#F4F9FF] transition-colors"
            data-testid="floating-contact-call"
            data-analytics="floating_contact_call"
            style={{ animationDelay: "60ms" }}
          >
            <span className="w-9 h-9 rounded-full bg-[#1677C8] text-white inline-flex items-center justify-center">
              <Phone size={18} strokeWidth={2.5} />
            </span>
            <span className="flex flex-col leading-tight text-left">
              <span className="text-xs uppercase tracking-widest text-[#4B6B86]">Call</span>
              <span className="text-sm">{business.phone}</span>
            </span>
          </a>
          <a
            href={mailHref}
            onClick={() => {
              trackEvent("floating_contact_email_click", { location: "floating-menu", cta: "email", email: business.email });
              setOpen(false);
            }}
            className="fc-action group flex items-center gap-3 pl-4 pr-5 py-3 rounded-full bg-white shadow-[0_12px_30px_rgba(20,50,74,0.18)] border border-[#E5F0FA] text-[#14324A] font-semibold hover:bg-[#F4F9FF] transition-colors"
            data-testid="floating-contact-email"
            data-analytics="floating_contact_email"
            style={{ animationDelay: "120ms" }}
          >
            <span className="w-9 h-9 rounded-full bg-[#23B7C5] text-white inline-flex items-center justify-center">
              <Mail size={18} strokeWidth={2.5} />
            </span>
            <span className="flex flex-col leading-tight text-left">
              <span className="text-xs uppercase tracking-widest text-[#4B6B86]">Email</span>
              <span className="text-sm break-all max-w-[220px] truncate">{business.email}</span>
            </span>
          </a>
        </div>
      )}

      {/* Trigger */}
      <button
        type="button"
        aria-label={open ? "Close contact options" : "Open contact options"}
        aria-expanded={open}
        onClick={() => {
          const next = !open;
          if (next) trackEvent("floating_contact_open", { location: "floating-menu" });
          setOpen(next);
        }}
        data-testid="floating-contact-toggle"
        data-analytics="floating_contact_toggle"
        className={`relative w-14 h-14 md:w-16 md:h-16 rounded-full inline-flex items-center justify-center text-white shadow-[0_16px_36px_rgba(22,119,200,0.35)] transition-all duration-200 active:scale-95 focus:outline-none focus:ring-4 focus:ring-[#1677C8]/30 ${
          open ? "bg-[#14324A]" : "bg-[#1677C8] hover:bg-[#14324A]"
        }`}
      >
        {/* Pulse ring — only when closed */}
        {!open && (
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full bg-[#1677C8]/60 animate-ping"
            style={{ animationDuration: "2.4s" }}
          />
        )}
        <span className="relative">
          {open ? <X size={22} strokeWidth={2.5} /> : <MessageCircle size={24} strokeWidth={2.5} />}
        </span>
      </button>
    </div>
  );
};

export default FloatingContact;
